import {
  PropsWithChildren,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { FlattenInterpolation, ThemeProps } from 'styled-components';
import { TextFieldProps, TextInput } from './TextInput';
import { useCursor } from '../../../util/useCursor';
import { ThemeType } from '../../../styling';
import { FormInput, useIsAutoSave, useModelForm } from '../modelInputs';

export type NumericInputProps = Partial<TextFieldProps> & {
  field: string,
  valueOverride?: string,
  errorOverride?: string,
  containerCss?: FlattenInterpolation<ThemeProps<ThemeType>> | string,
  onVisibleChange?: (visible: boolean) => void,
  validateValue?: (newValue: string) => boolean,
  touchOverride?: boolean,
}

export function NewNumericInput({
  id,
  field,
  valueOverride,
  errorOverride,
  containerCss,
  onVisibleChange,
  validateValue = () => true,
  onChange,
  onBlur,
  onFocus,
  mutateValue = (value) => value,
  children,
  ...props
}: PropsWithChildren<NumericInputProps>) {
  const {
    visible, errors, value, update, warnings,
  } = useModelForm(field, valueOverride, errorOverride);
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const changed = useRef<boolean>(false);
  const [changeCount, setChangeCount] = useState(0);

  const [recordCursor, restoreCursor] = useCursor(inputRef.current, focused);

  const { isAutoSave } = useIsAutoSave();

  const handleFocus = () => {
    setFocused(true);
    onFocus?.();
  };

  const handleBlur = (targetValue: string) => {
    setFocused(false);
    onBlur?.(targetValue);
    if (changed.current && isAutoSave) {
      update({ [field]: targetValue ? Number(mutateValue(targetValue).replace(/[^0-9.-]+/g, '')) : null });
    }
    changed.current = false;
  };

  const handleChange = (newValue: string) => {
    changed.current = true;
    recordCursor();
    setChangeCount((prev) => prev + 1);
    if (!validateValue(newValue)) {
      return;
    }
    update(
      { [field]: mutateValue(newValue) },
      isAutoSave,
    );
    onChange?.(newValue);
  };

  useEffect(() => {
    onVisibleChange?.(visible);
  }, [visible]);

  useLayoutEffect(() => {
    // Prevent cursor jumping
    restoreCursor();
  }, [value, changeCount]);

  return (
    <FormInput visible={visible} css={containerCss}>
      <TextInput
        {...props}
        id={id ?? field}
        ref={inputRef}
        errors={errors}
        warnings={warnings}
        visible={visible}
        value={value}
        mutateValue={mutateValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
      >
        {children}
      </TextInput>
    </FormInput>
  );
}
